import type { AboutData } from '../../models/About';
import { sanitizeRichHtml } from '@lumora/ui';
import EditableField from '../component/EditableField';

interface AboutViewProps {
    content: AboutData | null;
    navigate: (page: string) => void;
    isEditMode?: boolean;
    setContent?: (data: AboutData) => void;
    onSave?: (data: AboutData) => void;
}

// Fallback HTML to display if the database has nothing saved yet
const defaultMainText = `<h2 class="text-2xl font-bold text-red-700 mb-3"><i class="fas fa-users mr-2"></i>A Family Business Built on Trust</h2><p>Welcome to Al Amir Auto, your friendly neighborhood car dealership right here in Rochester! We're a proud father-and-son team dedicated to providing our community with reliable, quality pre-owned vehicles at fair prices. We believe buying a car should be a positive and transparent experience, and that's the foundation of our business.</p><p>With Dad bringing over <strong>30+ years of hands-on mechanic experience</strong>, we have a deep understanding of cars inside and out. Every vehicle on our lot is carefully inspected and selected, ensuring it meets our high standards for safety and reliability. We're not just selling cars; we're building relationships based on honesty and integrity.</p>`;
const defaultApproachText = `<h2 class="text-2xl font-bold text-red-700 mb-3"><i class="fas fa-thumbs-up mr-2"></i>Our Approach to Quality</h2><p>We know that buying a used car can feel daunting. That's why we put extra effort into making sure you feel confident in your purchase. Our experienced mechanic thoroughly checks each vehicle before it's offered for sale. We focus on transparency, providing you with as much information as possible about the vehicle's condition and history.</p><p>We stand behind the quality of our selection, but we also believe in empowering our customers. While our experienced mechanic meticulously inspects every vehicle, <strong>we always encourage you to have the vehicle inspected by your own trusted mechanic before purchase</strong>. Your peace of mind is our priority.</p>`;

const AboutView = ({ content, navigate, isEditMode = false, setContent, onSave }: AboutViewProps) => {
        
    // safe nested updates that builds the object even if 'content' is currently null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFieldSetter = (fieldKey: string) => (updatedField: any) => {
        if (!setContent) return;
        setContent({
            ...(content || {}),
            aboutPage: {
                ...(content?.aboutPage || {}),
                [fieldKey]: updatedField
            }
        } as AboutData);
    };

    // Ensure we ALWAYS have an object with a 'value' property to pass to EditableField
    const mainTextObj = content?.aboutPage?.mainTextHTML || { value: defaultMainText };
    const approachTextObj = content?.aboutPage?.approachTextHTML || { value: defaultApproachText };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 w-full flex-grow">
            <div className="bg-white p-8 md:p-12 rounded-xl shadow-sm border border-gray-100">
                <h1 className="text-4xl md:text-5xl font-bold text-center mb-10 uppercase text-gray-800">About Al Amir Auto</h1>
                <div className="space-y-10 text-lg leading-relaxed text-gray-700">
                    
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                        <img 
                            src="/assets/owners.png" 
                            alt="Father and Son Team" 
                            className="w-40 md:w-48 h-auto rounded-lg shadow-lg border-2 border-gray-200 flex-shrink-0" 
                        />
                        <div className="flex-grow">
                            {isEditMode ? (
                                <EditableField
                                    contentKey="value"
                                    content={mainTextObj}
                                    setContent={createFieldSetter('mainTextHTML')}
                                    isEditMode={isEditMode}
                                    onSave={onSave}
                                />
                            ) : (
                                <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(mainTextObj.value || '') }} />
                            )}
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-8">
                        {isEditMode ? (
                            <EditableField
                                contentKey="value"
                                content={approachTextObj}
                                setContent={createFieldSetter('approachTextHTML')}
                                isEditMode={isEditMode}
                                onSave={onSave}
                            />
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(approachTextObj.value || '') }} />
                        )}
                    </div>
                    
                    <div className="text-center border-t border-gray-200 pt-10">
                        <h3 className="text-3xl font-bold text-gray-800 mb-3">Ready to Find Your Next Car?</h3>
                        <p className="text-gray-600 mb-6">Get pre-approved for financing today and browse our quality inventory.</p>
                        <button 
                            onClick={() => navigate('listings')} 
                            className="inline-block bg-red-600 text-white px-10 py-3 rounded-md text-lg font-semibold hover:bg-red-700 shadow-lg transform hover:-translate-y-1 transition"
                        >
                            View Our Listings
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutView;